import { useLocation, useRouter } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Layout } from "@/components/layout";
import { VisitBrowser } from "@/components/layout/v3/VisitBrowser";
import { AlertMessage } from "@/components/ui/AlertMessage";
import { Button } from "@/components/ui/button";
import { GuidedChecklist } from "@/components/ui/guided/GuidedChecklist";
import {
  type PracticeMode,
  PracticeVisitDialog,
} from "@/components/ui/guided/PracticeVisit";
import { OnboardingCard } from "@/components/ui/onboarding/onboarding-card";
import { LoadErrorBanner } from "@/components/ui/redesign/page";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { EmptyVisits } from "@/components/ui/visits/empty-visits";
import {
  NewVisitSidebar,
  type PracticeVisitSource,
} from "@/components/ui/visits/new-visit-sidebar";
import {
  PreChartButton,
  PreChartSheet,
} from "@/components/ui/visits/PreChartSheet";
import { VisitList } from "@/components/ui/visits/visits-list";
import { isFeatureOn, useFeature } from "@/lib/early-access";
import {
  normalizeLaunchTemplate,
  resolveLaunchTemplate,
} from "@/lib/emr-launch";
import { loadPracticeAudio } from "@/lib/guided/practice";
import {
  matchPracticeVisit,
  rememberPracticeVisit,
} from "@/lib/guided/practice-visits";
import {
  isOnboarded,
  isPostStepSeen,
  markWelcomeSeen,
  shouldShowOnboarding,
  startFirstRun,
} from "@/lib/onboarding";
import { container } from "@/lib/utils";
import { useVisitList } from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";
import { useGuidedChecklistStore } from "@/store/guidedChecklist.store";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";
import { useWhatsNewStore } from "@/store/whatsNew.store";
import { useWorkspacePanelStore } from "@/store/workspacePanel.store";
import {
  NewVisitButton,
  VisitsEmptyState,
  VisitsHome,
} from "./redesign/VisitsHome";

export const Visits = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Pre-chart (VITE_PRE_CHART_ENABLED): beside New visit.
  const preChart = AppConfig.enablePreChart;
  const [preChartOpen, setPreChartOpen] = useState(false);
  const openPreChart = preChart
    ? () => {
        setPreChartOpen(true);
      }
    : undefined;
  const accountPublicId = useAuthStore((state) => state.userData?.public_id);
  // The welcome shows once per account. It is marked seen when it opens, so
  // a reload or another tab does not show it again; on this page it stays
  // open until it is finished or closed.
  const [welcomeFor, setWelcomeFor] = useState<string | null>(null);
  const [closedFor, setClosedFor] = useState<string | null>(null);
  useEffect(() => {
    if (accountPublicId === undefined || welcomeFor === accountPublicId) return;
    if (!shouldShowOnboarding(accountPublicId)) return;
    markWelcomeSeen(accountPublicId);
    setWelcomeFor(accountPublicId);
  }, [accountPublicId, welcomeFor]);
  const onboardingOpen =
    accountPublicId !== undefined &&
    welcomeFor === accountPublicId &&
    closedFor !== accountPublicId;
  const [postStepOpen, setPostStepOpen] = useState(false);
  const firstRecordingDone = usePostOnboardingStore((state) => state.done);
  const previousFirstRecordingDoneRef = useRef(firstRecordingDone);
  const { files, loading, error, ready, refresh } = useVisitList();
  // A brand-new account: the welcome is on screen and there are no visits.
  const firstRun = onboardingOpen && ready && !error && files.length === 0;
  useEffect(() => {
    if (!firstRun) return;
    startFirstRun(accountPublicId);
    // For a brand-new account everything in this release's "What's new" is
    // new: it is marked seen, so the sheet opens only for later releases
    // (and stays in the menu).
    useWhatsNewStore.getState().markSeen(accountPublicId);
    // Guided first visit: a brand-new account gets the "first steps" list.
    if (isFeatureOn("guided-first-visit")) {
      useGuidedChecklistStore.getState().load(accountPublicId);
      useGuidedChecklistStore.getState().start();
    }
  }, [firstRun, accountPublicId]);

  // Guided first visit: the practice consulta (sample recording or script).
  const guided = useFeature("guided-first-visit");
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceError, setPracticeError] = useState(false);
  const [practice, setPractice] = useState<PracticeVisitSource | null>(null);
  const openPractice = (): void => {
    setPracticeError(false);
    setPracticeOpen(true);
  };
  const choosePractice = (mode: PracticeMode): void => {
    const language = i18n.resolvedLanguage ?? i18n.language;
    if (mode === "read") {
      setPractice({ mode, language });
      setPracticeOpen(false);
      setSidebarOpen(true);
      return;
    }
    setPracticeLoading(true);
    setPracticeError(false);
    void loadPracticeAudio(language)
      .then((file) => {
        setPractice({ mode, file, language });
        setPracticeOpen(false);
        setSidebarOpen(true);
      })
      .catch(() => {
        setPracticeError(true);
      })
      .finally(() => {
        setPracticeLoading(false);
      });
  };
  const practiceVisitId = guided
    ? (files.find((visit) => matchPracticeVisit(accountPublicId, visit))
        ?.public_id ?? null)
    : null;
  const router = useRouter();
  const location = useLocation();

  useEffect(() => {
    const previous = previousFirstRecordingDoneRef.current;
    previousFirstRecordingDoneRef.current = firstRecordingDone;
    if (
      !previous &&
      firstRecordingDone &&
      isOnboarded(accountPublicId) &&
      !isPostStepSeen() &&
      // Guided first visit: the "Paste into your EMR" panel after the first
      // copy and the checklist offer the extension instead.
      !isFeatureOn("guided-first-visit")
    ) {
      setPostStepOpen(true);
    }
  }, [firstRecordingDone, accountPublicId]);

  // "What's new" waits while the welcome (or its after-recording step) is
  // open, and comes after it.
  const welcomeOpen = onboardingOpen || postStepOpen;
  useEffect(() => {
    useWhatsNewStore.getState().setWelcomeOpen(welcomeOpen);
  }, [welcomeOpen]);
  useEffect(
    () => (): void => {
      useWhatsNewStore.getState().setWelcomeOpen(false);
    },
    [],
  );

  const [launchTemplate, setLaunchTemplate] = useState<string | undefined>();
  useEffect(() => {
    if (location.search.openPreChart === true) {
      if (AppConfig.enablePreChart) setPreChartOpen(true);
      router.history.replace(location.pathname, {});
      return;
    }
    if (location.search.openNewVisit == true) {
      setLaunchTemplate(
        resolveLaunchTemplate(
          normalizeLaunchTemplate(location.search.template),
        ),
      );
      setSidebarOpen(true);
      router.history.replace(location.pathname, {});
    }
  }, [router.history, location.pathname, location.search]);

  const checklist = guided ? (
    <GuidedChecklist
      practiceVisitId={practiceVisitId}
      onOpenVisit={(visitPublicId) => {
        void router.navigate({
          to: "/visits/$visitPublicId",
          params: { visitPublicId },
        });
      }}
      onReopenWelcome={() => {
        if (accountPublicId === undefined) return;
        setClosedFor(null);
        setWelcomeFor(accountPublicId);
      }}
      onStartPractice={openPractice}
    />
  ) : null;

  const redesign = AppConfig.enableAppRedesign;
  return (
    <Layout
      // The redesign shows its own skeleton under the page header.
      isLoading={redesign ? false : loading}
      navbarTitle={t("visits.title")}
      // Phones and tablets: the visits screen has its own header (v3.2).
      topBar={redesign ? "desktop" : undefined}
      pageHeader={
        redesign && ready && files.length > 0
          ? {
              count: files.length,
              countLabel: t("visits.home.count", { count: files.length }),
              actions: (
                <>
                  {openPreChart && <PreChartButton onClick={openPreChart} />}
                  <NewVisitButton
                    compact
                    onClick={() => {
                      setSidebarOpen(true);
                    }}
                  />
                </>
              ),
            }
          : undefined
      }
      breadcrumbs={[
        { name: t("visits.title"), href: "/visits", current: true },
      ]}
    >
      <div
        className={
          redesign
            ? undefined
            : "min-h-screen supports-[min-height:100dvh]:min-h-dvh p-6"
        }
      >
        {redesign && (
          <div className="lg:hidden">
            <VisitBrowser
              empty={
                <VisitsEmptyState
                  onNewVisit={() => {
                    setSidebarOpen(true);
                  }}
                  onPreChart={openPreChart}
                />
              }
              loading={loading && !ready}
              mode="page"
              notice={
                (checklist || error) && (
                  <div className="space-y-3">
                    {checklist}
                    {error && (
                      <LoadErrorBanner
                        description={
                          ready
                            ? t("visits.home.loadError.stale")
                            : t("visits.home.loadError.empty")
                        }
                        title={t("error.unknown")}
                        onRetry={() => {
                          void refresh();
                        }}
                      />
                    )}
                  </div>
                )
              }
              visits={files}
            />
          </div>
        )}
        {redesign ? (
          <div className="max-lg:hidden">
            <VisitsHome
              notice={checklist}
              error={error}
              loading={loading}
              ready={ready}
              visits={files}
              onNewVisit={() => {
                setSidebarOpen(true);
              }}
              onPreChart={openPreChart}
              onRetry={() => {
                void refresh();
              }}
            />
          </div>
        ) : (
          <motion.div
            animate="show"
            className="mx-auto max-w-5xl space-y-8"
            initial="hidden"
            variants={container}
          >
            {error && (
              <div role="alert">
                <AlertMessage
                  description={
                    ready
                      ? t("visits.listError.stale")
                      : t("visits.listError.failed")
                  }
                  title={t("error.unknown")}
                  variant="destructive"
                />
                <Button
                  onClick={() => {
                    void refresh();
                  }}
                >
                  {t("common.restartList")}
                </Button>
              </div>
            )}
            {ready && files.length === 0 ? (
              <EmptyVisits
                onOpenSidebar={() => {
                  setSidebarOpen(true);
                }}
              />
            ) : ready ? (
              <div className="p-4">
                <div className="flex justify-end mb-6">
                  <SubscriptionOnlyButton
                    validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                    onClick={() => {
                      setSidebarOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("visits.newVisitButton")}
                  </SubscriptionOnlyButton>
                </div>
                <VisitList
                  visits={files}
                  refresh={() => {
                    void refresh();
                  }}
                />
              </div>
            ) : null}
          </motion.div>
        )}
        <NewVisitSidebar
          open={sidebarOpen}
          practice={practice}
          onOpenChange={(open, reason) => {
            setSidebarOpen(open);
            if (open) return;
            setPractice(null);
            setLaunchTemplate(undefined);
            // v3.2: Back or Cancel returns to the visit New visit was
            // started from; after a submission the visits list stays.
            const { returnTo } = useWorkspacePanelStore.getState();
            useWorkspacePanelStore.getState().set({ returnTo: null });
            if (reason !== "submitted" && returnTo)
              void router.history.push(returnTo);
          }}
          preferredTemplate={launchTemplate}
          onVisitCreated={(visitPublicId) => {
            if (practice) rememberPracticeVisit(accountPublicId, visitPublicId);
            void router.navigate({
              to: "/visits/$visitPublicId",
              params: { visitPublicId },
            });
          }}
        />
        {preChart && (
          <PreChartSheet
            open={preChartOpen}
            onCreated={(visitPublicId) => {
              void router.navigate({
                to: "/visits/$visitPublicId",
                params: { visitPublicId },
              });
            }}
            onOpenChange={setPreChartOpen}
          />
        )}
        <OnboardingCard
          firstRun={firstRun}
          open={onboardingOpen || postStepOpen}
          postStep={!onboardingOpen && postStepOpen}
          onOpenChange={(open) => {
            if (!open && accountPublicId !== undefined)
              setClosedFor(accountPublicId);
            setPostStepOpen(open);
          }}
          onStartRecording={() => {
            setSidebarOpen(true);
          }}
          onStartPractice={guided ? openPractice : undefined}
          onCreateTemplate={
            guided
              ? () => {
                  void router.navigate({ to: "/template/new" });
                }
              : undefined
          }
        />
        {guided && (
          <PracticeVisitDialog
            error={practiceError}
            loading={practiceLoading}
            open={practiceOpen}
            onChoose={choosePractice}
            onOpenChange={setPracticeOpen}
          />
        )}
      </div>
    </Layout>
  );
};
